import * as React from 'react';
import ChartComponent, { ChartComponentProps } from 'react-chartjs-2';

import Chart from 'chart.js';
import { ChartWrapper } from '../ChartWrapper';
import { DefaultChartProps, getOptionsForDefaultChartOptions } from '../DefaultChartProps';

export interface ChartableBarDataItem {
    label: string;
    value: number;
    colour: string;
}

export interface ChartableBarData<T extends ChartableBarDataItem = ChartableBarDataItem> {
    data: T[];
    label?: string;
}

export function toChartableBarData<T, U extends ChartableBarDataItem>(
    obj: T | null | undefined,
    mappingFunc: (data: T) => ChartableBarData<U>,
): ChartableBarData<U> | null {
    return obj ? mappingFunc(obj) : null;
}

export type BarChartDirection = 'Horizontal' | 'Vertical';

export interface BarChartProps extends DefaultChartProps {
    data: ChartableBarData | [ChartableBarData, ...ChartableBarData[]];
    direction?: BarChartDirection;
}

export const BarChart = React.forwardRef<HTMLDivElement, BarChartProps>(
    ({ data, direction = 'Vertical', options, otherOptions, ...props }, ref) => {
        const datasets = Array.isArray(data) ? data : [data];
        const chartData: Chart.ChartData = {
            datasets: datasets.map(x => ({
                backgroundColor: x.data.map(chartable => chartable.colour),
                data: x.data.map(chartable => chartable.value),
                label: x.label,
            })),
            labels: datasets[0].data.map(chartable => chartable.label),
        };
        let xAxes: Chart.ChartXAxe = {};
        let yAxes: Chart.ChartYAxe = {};
        let chartType: ChartComponentProps['type'];

        if (direction === 'Vertical') {
            yAxes = {
                ticks: {
                    beginAtZero: true,
                },
            };
            chartType = 'bar';
        } else {
            xAxes = {
                ticks: {
                    beginAtZero: true,
                },
            };
            chartType = 'horizontalBar';
        }

        return (
            <ChartWrapper ref={ref}>
                <ChartComponent
                    data={chartData}
                    legend={{
                        display: false,
                    }}
                    options={getOptionsForDefaultChartOptions({
                        helpers: {
                            xAxes,
                            yAxes,
                        },
                        options,
                        otherOptions,
                    })}
                    type={chartType}
                    {...props}
                />
            </ChartWrapper>
        );
    },
);
BarChart.displayName = 'BarChart';
